import DateStringInput from '@repo/ui/date-string-input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenters } from '../../api/centers/get-centers'
import { getCountries } from '../../api/countries/get-countries'
import { getFormulas } from '../../api/formula/get-formulas'
import { getOls } from '../../api/ols/get-ols'
import { getPeriods } from '../../api/periods/get-periods'
import useTranslate from '../../lib/hooks/use-translate'
import {
  CenterOlPaymentStatusEnum,
  centerOlPaymentStatusEnumMapping
} from '../../lib/types/enums/center-ol-payment/center-ol-payment-status.enum'
import {
  CenterOlPaymentTypeEnum,
  centerOlPaymentTypeMapping
} from '../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import { CurrencyEnum, currencyEnumMapping } from '../../lib/types/enums/common/currency.enum'
import { OLTypeEnum, olTypeEnumMapping } from '../../lib/types/enums/ol/ol-type.enum'
import { CenterFilter } from '../../lib/types/models/center/center-filter'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'
import { getOlName } from '../ols/center/utils/functions'

type Props = {
  filter?: CenterFilter
  onFilterChange: (filter: CenterFilter) => void
}

export function CenterPaymentsFilter({ filter, onFilterChange }: Props) {
  const { t } = useTranslate()

  function setFilter<K extends keyof CenterFilter>(key: K, value: CenterFilter[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  const { data: ols } = useQuery({
    queryKey: ['ols'],
    queryFn: () => getOls()
  })

  const { data: centers } = useQuery({
    queryKey: ['centers', 'BASIC'],
    queryFn: () => getCenters({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas', 'LIGHT'],
    queryFn: () => getFormulas({ version: 'LIGHT' })
  })

  const { data: periods } = useQuery({
    queryKey: ['periods', 'BASIC'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  return (
    <div className="flex flex-col gap-4 pt-2">
      <MultiSelectInput
        label={'Type de presta'}
        variant={'primary'}
        options={[OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE].map((t) => ({
          value: t,
          label: olTypeEnumMapping[t].label
        }))}
        selectedOptions={
          filter?.olTypes
            ? filter.olTypes.map((olType) => ({
                value: olType,
                label: olTypeEnumMapping[olType].label
              }))
            : undefined
        }
        onChange={(olTypes) => setFilter('olTypes', olTypes)}
      />

      <SelectInput
        label={'Nom de presta'}
        options={ols?.data.map((ol) => ({ label: getOlName(ol), value: ol.id })) ?? []}
        selectedOption={
          filter?.olIds?.[0]
            ? {
                value: filter.olIds[0],
                label: getOlName(ols?.data.find((ol) => ol.id === filter.olIds?.[0]))
              }
            : undefined
        }
        onChange={(olId) => setFilter('olIds', [olId])}
      />

      <SelectInput
        searchable
        label="Centre"
        options={
          centers?.data.map((center) => ({
            label: center.name,
            value: center.id
          })) || []
        }
        onChange={(option) => setFilter('centerId', option)}
      />

      <DateStringInput
        mode="dynamic"
        label={'Date du virement'}
        value={filter?.dateOfPayment}
        onChange={(dateOfPayment) => setFilter('dateOfPayment', dateOfPayment ?? undefined)}
      />

      <MultiSelectInput
        label={'Pays'}
        variant={'primary'}
        searchable
        options={countries?.data?.map((country) => ({ label: country.name, value: country.id }))}
        onChange={(countryIds) => setFilter('countryIds', countryIds)}
        selectedOptions={filter?.countryIds?.map((countryId) => ({
          label: countries?.data?.find((c) => c.id === countryId)?.name || '',
          value: countryId
        }))}
      />

      <MultiSelectInput
        label={'Formule'}
        variant={'primary'}
        searchable
        options={formulas?.data?.map((formula) => ({
          label: getTranslatedName(formula),
          value: getTranslatedName(formula)
        }))}
        onChange={(formulas) => setFilter('formulas', formulas)}
        selectedOptions={filter?.formulas?.map((formula) => ({
          label: getTranslatedName(formulas?.data?.find((f) => getTranslatedName(f) === formula)),
          value: formula
        }))}
      />

      <MultiSelectInput
        label={'Période(s)'}
        variant={'warning'}
        searchable
        options={periods?.data?.map((period) => ({ label: period.name, value: period.name }))}
        onChange={(periods) => setFilter('periods', periods)}
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data?.find((p) => p.name === period)?.name || '',
          value: period
        }))}
      />

      <SelectInput
        label="Type de virement"
        options={Object.values(CenterOlPaymentTypeEnum).map((value) => ({
          label: centerOlPaymentTypeMapping[value].label,
          value: value
        }))}
        selectedOption={
          filter?.type
            ? { label: centerOlPaymentTypeMapping[filter.type].label, value: filter.type }
            : undefined
        }
        onChange={(option) => setFilter('type', option)}
      />

      <SelectInput
        label="Statuts de virement"
        options={Object.values(CenterOlPaymentStatusEnum).map((value) => ({
          label: t(centerOlPaymentStatusEnumMapping[value].label),
          value: value
        }))}
        selectedOption={
          filter?.status
            ? {
                label: t(centerOlPaymentStatusEnumMapping[filter.status].label),
                value: filter.status
              }
            : undefined
        }
        onChange={(option) => setFilter('status', option)}
      />

      <SelectInput
        label="Par devise"
        options={Object.values(CurrencyEnum).map((value) => ({
          label: currencyEnumMapping[value].label,
          value: value
        }))}
        selectedOption={
          filter?.currency
            ? {
                label: currencyEnumMapping[filter.currency].label,
                value: filter.currency
              }
            : undefined
        }
        onChange={(option) => setFilter('currency', option)}
      />

      <div className="flex flex-col gap-2">
        <Typography variant="label-medium">Facture(s)</Typography>

        <div className="flex flex-row gap-2">
          <RadioInput
            checked={filter?.hasInvoices === undefined}
            label="Tous"
            onCheck={() => setFilter('hasInvoices', undefined)}
          />
          <RadioInput
            checked={filter?.hasInvoices === true}
            label="Oui"
            onCheck={() => setFilter('hasInvoices', true)}
          />
          <RadioInput
            checked={filter?.hasInvoices === false}
            label="Non"
            onCheck={() => setFilter('hasInvoices', false)}
          />
        </div>
      </div>
    </div>
  )
}
